import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Button, Grid, Group, Paper, Stack, Tabs, Title } from "@mantine/core";
import { debounce } from "lodash";
import { Client, CreateCampaignInquiryInput } from "src/graphql/generated/graphql";
import { DeepPartial } from "src/types/utils";

import { handleError } from "../../utils/error";
import {
    CreateCampaignInquiryAdditionalNotesForm,
    CreateCampaignInquiryDeliveryForm,
    CreateCampaignInquiryFormProvider,
    CreateCampaignInquiryGeneralForm,
    CreateCampaignInquiryPricingForm,
    CreateCampaignInquiryProductsForm,
    CreateCampaignInquiryQuestionnaireForm,
    useCreateCampaignInquiryForm,
} from "../CreateCampaignInquiryForm";
import { CreateCampaignInquiryFormValues } from "../CreateCampaignInquiryForm/formContext";
import { createCampaignInquiryFormKey, getDefaultValues } from "../CreateCampaignInquiryForm/utils";
import { CreateCampaignInquiryTab } from "./tab";

export interface CreateCampaignInquiryProps {
    client: Client;
    onCreate?: (campaignInquiry: CreateCampaignInquiryInput) => Promise<boolean | void> | boolean | void;
    initialValues?: DeepPartial<CreateCampaignInquiryFormValues>;
    isSkipLocalStorage?: boolean;
}

const DEFAULT_QUESTIONNAIRE_QUESTIONS_LIMIT = 5;

export const CreateCampaignInquiry = ({
    client,
    onCreate,
    initialValues,
    isSkipLocalStorage,
}: CreateCampaignInquiryProps) => {
    const { t } = useTranslation(["common"]);
    const navigate = useNavigate();

    const [activeIndex, setActiveIndex] = useState(0);

    const createCampaignInquiryForm = useCreateCampaignInquiryForm({
        initialValues: initialValues || getDefaultValues(client, isSkipLocalStorage),
    });

    const errorsJson = JSON.stringify(createCampaignInquiryForm.errors);

    useEffect(() => {
        if (!createCampaignInquiryForm.isDirty() || !createCampaignInquiryForm.values.clientId) {
            return;
        }
        const handleSaveToLocalStorage = debounce(() => {
            localStorage.setItem(createCampaignInquiryFormKey, JSON.stringify(createCampaignInquiryForm.values));
        }, 1000);

        handleSaveToLocalStorage();

        return () => {
            handleSaveToLocalStorage.cancel();
        };
        // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(createCampaignInquiryForm.values)]);

    const clearLocalStorage = useCallback(() => {
        localStorage.removeItem(createCampaignInquiryFormKey);
        createCampaignInquiryForm.reset();
        createCampaignInquiryForm.setValues(getDefaultValues(client));
        // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client]);

    const handleSubmit = useCallback(
        async (campaignInquiry: CreateCampaignInquiryInput) => {
            try {
                const result = await onCreate?.(campaignInquiry);
                // Only clear localStorage if onCreate was successful
                if (result !== false) {
                    clearLocalStorage();
                }
            } catch (error) {
                handleError(error, {
                    message: { key: "campaigninquiry_saving_failed", namespace: "common" },
                });
            }
        },
        [clearLocalStorage, onCreate]
    );

    const handleNextStep = useCallback((event) => {
        event?.preventDefault?.();

        setActiveIndex((activeIndex) => activeIndex + 1);
    }, []);

    const handlePreviousStep = useCallback((event) => {
        event?.preventDefault?.();

        setActiveIndex((activeIndex) => activeIndex - 1);
    }, []);

    const tabs = [
        {
            title: t("campaigninquiry_create_tab_settings"),
            content: (
                <Stack>
                    <Paper p={36} radius={12} pos="relative">
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_general")}
                        </Title>
                        <CreateCampaignInquiryGeneralForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_pricing")}
                        </Title>
                        <CreateCampaignInquiryPricingForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaign_editor_delivery")}
                        </Title>
                        <CreateCampaignInquiryDeliveryForm />
                    </Paper>
                    <Paper p={36} radius={12}>
                        <Title mb={28} order={3} size={16} weight={600}>
                            {t("campaigninquiry_additionalnotes")}
                        </Title>
                        <CreateCampaignInquiryAdditionalNotesForm />
                    </Paper>
                </Stack>
            ),
            hasError: useCallback(() => {
                return ["tenantId", "displayTenantIds", "name"].reduce(
                    (hasError, field) => hasError || !!createCampaignInquiryForm.errors[field],
                    false
                );
                // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errorsJson]),
        },
        {
            title: t("campaigninquiry_create_tab_products"),
            content: <CreateCampaignInquiryProductsForm />,
            hasError: useCallback(() => {
                for (const field of Object.keys(createCampaignInquiryForm.errors)) {
                    if (/^products\./.test(field) && !!createCampaignInquiryForm.errors[field]) {
                        return true;
                    }
                }

                return false;
                // if form was in dependency array, it would cause infinite rerenders... some things are not memoized in this version mantine form
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [errorsJson]),
        },
        {
            title: t("campaigninquiry_create_tab_questionare"),
            content: (
                <CreateCampaignInquiryQuestionnaireForm
                    questionsLimit={client?.maxQuestionnaireQuestions || DEFAULT_QUESTIONNAIRE_QUESTIONS_LIMIT}
                />
            ),
            hasError: () => false,
        },
    ];

    return (
        <Tabs
            styles={{
                tabsList: {
                    display: "flex",
                    flexDirection: "column",
                },
                tab: {
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 4,
                    gap: 20,
                    "&[data-active] .mantine-Tabs-tabLabel": {
                        background: "#EEEFF2",
                    },
                },
                tabLabel: {
                    paddingBlock: 8,
                    paddingInline: 12,
                    borderRadius: 8,
                },
                tabIcon: {
                    height: 12,
                    width: 12,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                },
            }}
            value={String(activeIndex)}
            onTabChange={(index) => setActiveIndex(Number(index))}
            unstyled
        >
            <Grid>
                <Grid.Col pr={30} lg="content">
                    <Tabs.List>
                        {tabs.map((tab, index) => (
                            <CreateCampaignInquiryTab
                                key={index}
                                value={String(index)}
                                isActive={index === activeIndex}
                                isCompleted={activeIndex > index}
                                isLast={index >= tabs.length - 1}
                                hasError={tab.hasError()}
                            >
                                {tab.title}
                            </CreateCampaignInquiryTab>
                        ))}
                    </Tabs.List>
                </Grid.Col>
                <Grid.Col lg="auto">
                    <CreateCampaignInquiryFormProvider form={createCampaignInquiryForm}>
                        <Box
                            component="form"
                            style={{ marginBottom: "4rem" }}
                            onSubmit={createCampaignInquiryForm.onSubmit(handleSubmit)}
                            onKeyDown={(event) => {
                                if (event?.key === "Enter") {
                                    event.preventDefault?.();
                                }
                            }}
                        >
                            {tabs.map(({ content }, index) => (
                                <Tabs.Panel key={index} value={String(index)}>
                                    <Paper mb={24} p={24} radius={12} withBorder sx={{ backgroundColor: "white" }}>
                                        <Title order={4} mb={16}>
                                            {t("inquiry_guide_title")}
                                        </Title>
                                        <Group spacing="md">
                                            <Button
                                                component="a"
                                                href="https://www.loom.com/share/49baa6a9ce2e480fb2fabf37c4fdda76"
                                                target="_blank"
                                                variant="outline"
                                                leftIcon={<span>📹</span>}
                                            >
                                                {t("video_tutorial_part1")}
                                            </Button>
                                            <Button
                                                component="a"
                                                href="https://www.loom.com/share/6b8def1d552c490685083fc12f555f03"
                                                target="_blank"
                                                variant="outline"
                                                leftIcon={<span>📹</span>}
                                            >
                                                {t("video_tutorial_part2")}
                                            </Button>
                                        </Group>
                                    </Paper>
                                    {content}
                                    <Group position="apart" mt={24}>
                                        {index > 0 ? (
                                            <Button
                                                variant="default"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={handlePreviousStep}
                                            >
                                                {t("previousstep")}
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="default"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={() => navigate("/campaigns/inquiries")}
                                            >
                                                {t("cancel")}
                                            </Button>
                                        )}
                                        <Button
                                            variant="default"
                                            size="lg"
                                            color="primary.main"
                                            radius="md"
                                            style={{
                                                fontSize: 14,
                                            }}
                                            onClick={clearLocalStorage}
                                        >
                                            {t("campaigninquiry_clear")}
                                        </Button>
                                        {index < tabs.length - 1 ? (
                                            <Button
                                                variant="filled"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                                onClick={handleNextStep}
                                            >
                                                {t("nextstep")}
                                            </Button>
                                        ) : (
                                            <Button
                                                type="submit"
                                                variant="filled"
                                                size="lg"
                                                color="primary.main"
                                                radius="md"
                                                style={{
                                                    fontSize: 14,
                                                }}
                                            >
                                                {t("campaigninquiry_create")}
                                            </Button>
                                        )}
                                    </Group>
                                </Tabs.Panel>
                            ))}
                        </Box>
                    </CreateCampaignInquiryFormProvider>
                </Grid.Col>
            </Grid>
        </Tabs>
    );
};
